import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  LoginRequest, 
  LoginResponse, 
  Dealer, 
  CreateDealerRequest, 
  UpdateDealerRequest,
  EmbedCode,
  ServiceType,
  AdminUser,
  CreateAdminUserRequest,
  UpdateAdminUserRequest,
  DealerUser,
  CreateDealerUserRequest,
  UpdateDealerUserRequest,
  DealerLocation,
  CreateLocationRequest,
  UpdateLocationRequest,
  UpdateLocationHoursRequest,
  ServiceBay,
  CreateServiceBayRequest,
  UpdateServiceBayRequest,
  UpdateBayServicesRequest,
  DealerService,
  CreateDealerServiceRequest,
  UpdateDealerServiceRequest,
  Appointment,
  CreateAppointmentRequest,
  UpdateAppointmentRequest
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL = process.env.REACT_APP_API_URL || 'https://scheduler-api.ndproductsandbox.com/api';

  constructor() {
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors and token refresh
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 403 && 
            error.response?.data?.error?.includes('expired') && 
            !originalRequest._retry) {
          
          originalRequest._retry = true;
          
          try {
            // Try to refresh the token
            const refreshResponse = await axios.post(`${this.baseURL}/auth/refresh`, {}, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
              }
            });
            
            if (refreshResponse.data.success) {
              const newToken = refreshResponse.data.data.token;
              localStorage.setItem('adminToken', newToken);
              
              // Retry the original request with the new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }
        }
        
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Token expired, invalid, or refresh failed
          console.log('ApiService: Auth error detected:', error.response?.status, error.response?.data);
          console.log('ApiService: Request URL:', error.config?.url);
          console.log('ApiService: Full error:', error);
          
          // Don't redirect immediately for dealer users - let the component handle it
          const savedUser = localStorage.getItem('adminUser');
          if (savedUser) {
            const user = JSON.parse(savedUser);
            if (user.userType === 'dealer') {
              console.log('ApiService: Dealer user - not auto-redirecting, letting component handle auth error');
              return Promise.reject(error);
            }
          }
          
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  async refreshToken(): Promise<ApiResponse<LoginResponse>> {
    const response = await this.api.post('/auth/refresh');
    return response.data;
  }

  async logout(): Promise<ApiResponse<null>> {
    const response = await this.api.post('/auth/logout');
    return response.data;
  }

  async changePassword(data: { 
    currentPassword: string; 
    newPassword: string; 
    confirmPassword: string; 
  }): Promise<ApiResponse<null>> {
    const response = await this.api.put('/auth/change-password', data);
    return response.data;
  }

  // Dealer endpoints
  async getDealers(): Promise<ApiResponse<Dealer[]>> {
    const response = await this.api.get('/dealers');
    return response.data;
  }

  async getDealer(id: string): Promise<ApiResponse<Dealer>> {
    const response = await this.api.get(`/dealers/${id}`);
    return response.data;
  }

  async createDealer(data: CreateDealerRequest): Promise<ApiResponse<Dealer>> {
    const response = await this.api.post('/dealers', data);
    return response.data;
  }

  async updateDealer(id: string, data: Partial<UpdateDealerRequest>): Promise<ApiResponse<Dealer>> {
    const response = await this.api.put(`/dealers/${id}`, data);
    return response.data;
  }

  async deleteDealer(id: string): Promise<ApiResponse<null>> {
    const response = await this.api.delete(`/dealers/${id}`);
    return response.data;
  }

  async regenerateEmbedToken(id: string): Promise<ApiResponse<{ embedToken: string }>> {
    const response = await this.api.post(`/dealers/${id}/regenerate-token`);
    return response.data;
  }

  // Location management endpoints
  async getDealerLocations(dealerId: string): Promise<ApiResponse<DealerLocation[]>> {
    const response = await this.api.get(`/dealers/${dealerId}/locations`);
    return response.data;
  }

  async createLocation(dealerId: string, locationData: CreateLocationRequest): Promise<ApiResponse<DealerLocation>> {
    const response = await this.api.post(`/dealers/${dealerId}/locations`, locationData);
    return response.data;
  }

  async updateLocation(locationId: string, locationData: UpdateLocationRequest): Promise<ApiResponse<DealerLocation>> {
    const response = await this.api.put(`/locations/${locationId}`, locationData);
    return response.data;
  }

  async deleteLocation(locationId: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/locations/${locationId}`);
    return response.data;
  }

  async updateLocationHours(locationId: string, hoursData: UpdateLocationHoursRequest): Promise<ApiResponse<void>> {
    const response = await this.api.put(`/locations/${locationId}/hours`, hoursData);
    return response.data;
  }

  // Service bay management endpoints
  async getLocationBays(locationId: string): Promise<ApiResponse<ServiceBay[]>> {
    const response = await this.api.get(`/locations/${locationId}/bays`);
    return response.data;
  }

  async createServiceBay(locationId: string, bayData: CreateServiceBayRequest): Promise<ApiResponse<ServiceBay>> {
    const response = await this.api.post(`/locations/${locationId}/bays`, bayData);
    return response.data;
  }

  async updateServiceBay(bayId: string, bayData: UpdateServiceBayRequest): Promise<ApiResponse<ServiceBay>> {
    const response = await this.api.put(`/bays/${bayId}`, bayData);
    return response.data;
  }

  async deleteServiceBay(bayId: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/bays/${bayId}`);
    return response.data;
  }

  async getBayServices(bayId: string): Promise<ApiResponse<any[]>> {
    const response = await this.api.get(`/bays/${bayId}/services`);
    return response.data;
  }

  async updateBayServices(bayId: string, servicesData: UpdateBayServicesRequest): Promise<ApiResponse<void>> {
    const response = await this.api.put(`/bays/${bayId}/services`, servicesData);
    return response.data;
  }

  // Service management endpoints
  async getDealerServices(dealerId: string): Promise<ApiResponse<DealerService[]>> {
    const response = await this.api.get(`/dealers/${dealerId}/services`);
    return response.data;
  }

  async createDealerService(dealerId: string, serviceData: CreateDealerServiceRequest): Promise<ApiResponse<DealerService>> {
    const response = await this.api.post(`/dealers/${dealerId}/services`, serviceData);
    return response.data;
  }

  async updateDealerService(serviceId: string, serviceData: UpdateDealerServiceRequest): Promise<ApiResponse<DealerService>> {
    const response = await this.api.put(`/services/${serviceId}`, serviceData);
    return response.data;
  }

  async deleteDealerService(serviceId: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/services/${serviceId}`);
    return response.data;
  }

  async getEmbedCode(id: string): Promise<ApiResponse<EmbedCode>> {
    const response = await this.api.get(`/dealers/${id}/embed-code`);
    return response.data;
  }

  // TireWorks HD Services
  async getTireWorksServices(credentials: {
    apiEndpoint: string;
    locationId: string;
    applicationUID: string;
  }): Promise<ApiResponse<ServiceType[]>> {
    const response = await this.api.post('/dealers/services/fetch', credentials);
    return response.data;
  }

  // Fetch available TWHD services for a dealer using their stored credentials.
  // Returns { services: ServiceType[], allowedServices: string[] }
  async getDealerTwhdServices(dealerId: string): Promise<ApiResponse<{ services: ServiceType[]; allowedServices: string[] }>> {
    const response = await this.api.get(`/dealers/${dealerId}/twhd-services`);
    return response.data;
  }

  // Persist the dealer's TWHD service visibility selection (allowlist).
  // Empty array means all services are shown; non-empty = only listed IDs shown.
  async updateAllowedServices(dealerId: string, allowedServices: string[]): Promise<ApiResponse<{ allowedServices: string[] }>> {
    const response = await this.api.put(`/dealers/${dealerId}/allowed-services`, { allowedServices });
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/embed/health');
    return response.data;
  }

  // Dealer User Management
  async getDealerUsers(dealerId: string): Promise<ApiResponse<DealerUser[]>> {
    const response = await this.api.get(`/dealer-users/${dealerId}`);
    return response.data;
  }

  async createDealerUser(userData: CreateDealerUserRequest): Promise<ApiResponse<DealerUser>> {
    const response = await this.api.post('/dealer-users', userData);
    return response.data;
  }

  async updateDealerUser(userId: string, userData: UpdateDealerUserRequest): Promise<ApiResponse<DealerUser>> {
    const response = await this.api.put(`/dealer-users/${userId}`, userData);
    return response.data;
  }

  async deleteDealerUser(userId: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/dealer-users/${userId}`);
    return response.data;
  }

  // Appointment management endpoints
  async getAppointments(dealerId: string, locationId?: string, date?: string): Promise<ApiResponse<Appointment[]>> {
    let url = `/dealers/${dealerId}/appointments`;
    const params = new URLSearchParams();
    
    if (locationId) params.append('locationId', locationId);
    if (date) params.append('date', date);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await this.api.get(url);
    return response.data;
  }

  async getAppointment(appointmentId: string): Promise<ApiResponse<Appointment>> {
    const response = await this.api.get(`/appointments/${appointmentId}`);
    return response.data;
  }

  async createAppointment(appointmentData: CreateAppointmentRequest): Promise<ApiResponse<Appointment>> {
    const response = await this.api.post(`/dealers/${appointmentData.dealerId}/appointments`, appointmentData);
    return response.data;
  }

  async updateAppointment(appointmentId: string, appointmentData: UpdateAppointmentRequest): Promise<ApiResponse<Appointment>> {
    const response = await this.api.put(`/appointments/${appointmentId}`, appointmentData);
    return response.data;
  }

  async deleteAppointment(appointmentId: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/appointments/${appointmentId}`);
    return response.data;
  }

  async getLocationAppointments(locationId: string, date?: string): Promise<ApiResponse<Appointment[]>> {
    let url = `/locations/${locationId}/appointments`;
    if (date) {
      url += `?date=${date}`;
    }
    const response = await this.api.get(url);
    return response.data;
  }

  async getBayAppointments(bayId: string, date?: string): Promise<ApiResponse<Appointment[]>> {
    let url = `/bays/${bayId}/appointments`;
    if (date) {
      url += `?date=${date}`;
    }
    const response = await this.api.get(url);
    return response.data;
  }

  // Admin User Management (Admin Only)
  async getAdminUsers(): Promise<ApiResponse<AdminUser[]>> {
    const response = await this.api.get('/admin-users');
    return response.data;
  }

  async createAdminUser(userData: CreateAdminUserRequest): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.post('/admin-users', userData);
    return response.data;
  }

  async updateAdminUser(userId: string, userData: UpdateAdminUserRequest): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.put(`/admin-users/${userId}`, userData);
    return response.data;
  }

  async deleteAdminUser(userId: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/admin-users/${userId}`);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;